import { LinearSolver, SparseCsr } from './LinearSolver';

/**
 * Direct sparse solver for a symmetric-positive-definite system `A x = b` via an
 * LDLᵀ factorization (Tim Davis' compact LDL algorithm) with a Reverse Cuthill-McKee
 * fill-reducing ordering.
 *
 * Because `A` (the cotangent `L_ff`) is CONSTANT across an edit, it is factorized ONCE
 * in {@link prefactor}; every {@link solve} is then a cheap forward/diagonal/back
 * substitution — no iteration, no conditioning blow-up with mesh size. Drop-in for
 * {@link CgSolver} behind {@link LinearSolver} (the `x0` warm-start arg is ignored).
 */
export class CholeskySolver implements LinearSolver {
  private n = 0;
  private perm: Int32Array = new Int32Array(0); // perm[newIdx] = oldIdx
  private iperm: Int32Array = new Int32Array(0); // iperm[oldIdx] = newIdx

  // LDLᵀ factor (lower L with unit diagonal, stored by column; diagonal D).
  private Lp: Int32Array = new Int32Array(0);
  private Li: Int32Array = new Int32Array(0);
  private Lx: Float64Array = new Float64Array(0);
  private D: Float64Array = new Float64Array(0);

  // Solve workspace.
  private bperm: Float64Array = new Float64Array(0);

  public prefactor(a: SparseCsr): void {
    const n = a.n;
    this.n = n;
    this.perm = rcmOrder(a);
    this.iperm = new Int32Array(n);
    for (let i = 0; i < n; i++) this.iperm[this.perm[i]] = i;

    // Build the permuted upper-triangular matrix A' = P A Pᵀ in CSC, rows sorted ascending.
    const upper = this.buildPermutedUpperCsc(a);
    this.factorize(upper.Ap, upper.Ai, upper.Ax);
    this.bperm = new Float64Array(n);
  }

  public solve(b: Float64Array, _x0: Float64Array | null, out: Float64Array): void {
    const { n, perm, Lp, Li, Lx, D, bperm } = this;
    // Permute rhs into the factor's ordering.
    for (let i = 0; i < n; i++) bperm[i] = b[perm[i]];

    // Forward solve L y = bperm (unit lower).
    for (let j = 0; j < n; j++) {
      const yj = bperm[j];
      for (let p = Lp[j]; p < Lp[j + 1]; p++) bperm[Li[p]] -= Lx[p] * yj;
    }
    // Diagonal solve D z = y.
    for (let j = 0; j < n; j++) bperm[j] /= D[j];
    // Back solve Lᵀ x = z.
    for (let j = n - 1; j >= 0; j--) {
      let xj = bperm[j];
      for (let p = Lp[j]; p < Lp[j + 1]; p++) xj -= Lx[p] * bperm[Li[p]];
      bperm[j] = xj;
    }
    // Un-permute into the output.
    for (let i = 0; i < n; i++) out[perm[i]] = bperm[i];
  }

  /** Builds the permuted upper-triangular CSC (column j holds rows i ≤ j, ascending). */
  private buildPermutedUpperCsc(a: SparseCsr): { Ap: Int32Array; Ai: Int32Array; Ax: Float64Array } {
    const n = this.n;
    const { rowPtr, colIdx, values } = a;
    const iperm = this.iperm;

    // Count per new-column upper entries.
    const colCount = new Int32Array(n);
    for (let oldRow = 0; oldRow < n; oldRow++) {
      const nj = iperm[oldRow];
      for (let p = rowPtr[oldRow]; p < rowPtr[oldRow + 1]; p++) {
        const ni = iperm[colIdx[p]];
        if (ni <= nj) colCount[nj]++;
      }
    }
    const Ap = new Int32Array(n + 1);
    for (let j = 0; j < n; j++) Ap[j + 1] = Ap[j] + colCount[j];
    const nnz = Ap[n];
    const Ai = new Int32Array(nnz);
    const Ax = new Float64Array(nnz);
    const cursor = Ap.slice(0, n);
    for (let oldRow = 0; oldRow < n; oldRow++) {
      const nj = iperm[oldRow];
      for (let p = rowPtr[oldRow]; p < rowPtr[oldRow + 1]; p++) {
        const ni = iperm[colIdx[p]];
        if (ni <= nj) {
          const dst = cursor[nj]++;
          Ai[dst] = ni;
          Ax[dst] = values[p];
        }
      }
    }
    // Sort each column's rows ascending (insertion sort — columns are short).
    for (let j = 0; j < n; j++) {
      for (let p = Ap[j] + 1; p < Ap[j + 1]; p++) {
        const ai = Ai[p];
        const ax = Ax[p];
        let q = p - 1;
        while (q >= Ap[j] && Ai[q] > ai) {
          Ai[q + 1] = Ai[q];
          Ax[q + 1] = Ax[q];
          q--;
        }
        Ai[q + 1] = ai;
        Ax[q + 1] = ax;
      }
    }
    return { Ap, Ai, Ax };
  }

  /** Davis LDLᵀ symbolic + numeric factorization of upper-CSC `A`. */
  private factorize(Ap: Int32Array, Ai: Int32Array, Ax: Float64Array): void {
    const n = this.n;
    const parent = new Int32Array(n);
    const Lnz = new Int32Array(n);
    const flag = new Int32Array(n);

    // --- symbolic: elimination tree + column counts ---
    for (let k = 0; k < n; k++) {
      parent[k] = -1;
      flag[k] = k;
      Lnz[k] = 0;
      for (let p = Ap[k]; p < Ap[k + 1]; p++) {
        let i = Ai[p];
        if (i < k) {
          for (; flag[i] !== k; i = parent[i]) {
            if (parent[i] === -1) parent[i] = k;
            Lnz[i]++;
            flag[i] = k;
          }
        }
      }
    }
    const Lp = new Int32Array(n + 1);
    for (let k = 0; k < n; k++) Lp[k + 1] = Lp[k] + Lnz[k];
    const Li = new Int32Array(Lp[n]);
    const Lx = new Float64Array(Lp[n]);
    const D = new Float64Array(n);

    // --- numeric ---
    const Y = new Float64Array(n);
    const pattern = new Int32Array(n);
    const lnzRun = new Int32Array(n); // running per-column count
    for (let k = 0; k < n; k++) {
      let top = n;
      Y[k] = 0;
      flag[k] = k;
      for (let p = Ap[k]; p < Ap[k + 1]; p++) {
        let i = Ai[p];
        if (i > k) continue; // upper only
        Y[i] += Ax[p];
        let len = 0;
        for (; flag[i] !== k; i = parent[i]) {
          pattern[len++] = i;
          flag[i] = k;
        }
        while (len > 0) pattern[--top] = pattern[--len];
      }
      D[k] = Y[k];
      Y[k] = 0;
      for (; top < n; top++) {
        const i = pattern[top];
        const yi = Y[i];
        Y[i] = 0;
        const end = Lp[i] + lnzRun[i];
        for (let p = Lp[i]; p < end; p++) Y[Li[p]] -= Lx[p] * yi;
        const lki = yi / D[i];
        D[k] -= lki * yi;
        Li[end] = k;
        Lx[end] = lki;
        lnzRun[i]++;
      }
      // SPD guard: keep the pivot strictly positive (matrix is SPD by construction,
      // but clamp protects against floating-point edge cases / disconnected islands).
      if (D[k] <= 0) D[k] = 1e-12;
    }

    this.Lp = Lp;
    this.Li = Li;
    this.Lx = Lx;
    this.D = D;
  }
}

/** Reverse Cuthill-McKee ordering of the symmetric adjacency in `a` (off-diagonals). */
function rcmOrder(a: SparseCsr): Int32Array {
  const { n, rowPtr, colIdx } = a;
  const degree = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    let d = 0;
    for (let p = rowPtr[i]; p < rowPtr[i + 1]; p++) if (colIdx[p] !== i) d++;
    degree[i] = d;
  }

  const visited = new Uint8Array(n);
  const order = new Int32Array(n);
  let count = 0;
  const queue: number[] = [];

  while (count < n) {
    // Root = unvisited vertex of minimum degree (one per connected component).
    let root = -1;
    for (let i = 0; i < n; i++) {
      if (!visited[i] && (root < 0 || degree[i] < degree[root])) root = i;
    }
    if (root < 0) break;

    visited[root] = 1;
    queue.length = 0;
    queue.push(root);
    let head = 0;
    while (head < queue.length) {
      const node = queue[head++];
      order[count++] = node;
      // Unvisited neighbours, added in ascending-degree order (Cuthill-McKee).
      const nbrs: number[] = [];
      for (let p = rowPtr[node]; p < rowPtr[node + 1]; p++) {
        const j = colIdx[p];
        if (j !== node && !visited[j]) nbrs.push(j);
      }
      nbrs.sort((x, y) => degree[x] - degree[y]);
      for (const j of nbrs) {
        if (!visited[j]) {
          visited[j] = 1;
          queue.push(j);
        }
      }
    }
  }

  // Reverse → RCM. perm[newIdx] = oldIdx.
  const perm = new Int32Array(n);
  for (let i = 0; i < n; i++) perm[i] = order[n - 1 - i];
  return perm;
}
